import type { ReactElement } from 'react';

import { InlineEdit } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * `FE-08.1` — the board's `<h1>`, renameable in place. Chunk `D3`, agent D.
 *
 * `FR-BOARD-003`: "Selecting the board title in the board header turns it into
 * an editable field. Enter or blur commits; Escape cancels and restores the
 * previous title."
 *
 * ⚠ **The `<h1>` carries an explicit `aria-label={title}`, and that is not
 * decoration.** Mounting a control inside a labelling element makes the
 * control's markup part of the element's accessible name — the defect that made
 * every column announce itself as *"Backlog, edit Rename list"* and silently
 * stopped `getByRole('region', { name })` matching across five spec files. An
 * explicit label halts the traversal. This is defect-ledger item 3 and it lands
 * here exactly. `BoardTitle.test.tsx` asserts the `<h1>`'s accessible name is
 * exactly `title` **while the editor is open** — the assertion that would have
 * caught it.
 *
 * ⚠ **`commitOn="enter"`, not `"both"`.** `ListHeader` records a real focus
 * restoration race between `InlineEdit`'s blur commit and Radix's focus return
 * when the editor is opened from a menu: `commitOn="both"`'s `handleBlur` reads
 * the untouched draft as "no change, close" the instant Radix returns focus to
 * a trigger elsewhere. This file mounts no menu and nothing returns focus out
 * from under the input the way `ListHeader`'s did — but the requirement text
 * ("Enter or blur") is exactly what that file quotes before choosing `"enter"`
 * anyway, and D3's own brief (§6.4) fixes the same answer here. Kept at
 * `"enter"` rather than reopening a race this codebase has already paid to
 * find; see `ListHeader.tsx`'s comment for the full account.
 *
 * `emptyPolicy="reject-and-restore"` is `FR-BOARD-003` AC3 (an empty commit is
 * rejected and the previous title kept); Escape is AC2.
 *
 * ⚠ `canEdit` here means **the board is not closed** — `lib/board-face.ts`'s
 * `canEdit`, unchanged. Role-aware rendering (a member without edit permission
 * seeing a non-editable title) is `FR-BOARD-003`'s edge case, which defers to
 * `06-workspaces-and-permissions.md`, and is **`FE-09.2`'s, chunk `D4`**.
 *
 * `busy`/`error` are caller-driven (`onRename` returns `void`, not a promise) —
 * the board face's own `useRenameBoard` mutation drives them, the same shape
 * `ProfilePanel`'s `displayName` field uses for its `InlineEdit`. Styling: no
 * new colour token. The `<h1>` carries `text-shadow-on-board`, matching
 * `BoardFace`'s existing header exactly. `InlineEdit`'s idle control sets its
 * own ink, so the board title passes `text-on-board` to that control
 * explicitly; otherwise a photo board would paint the editable title with
 * normal `text-ink`.
 */

export interface BoardTitleProps {
  title: string;
  canEdit: boolean;
  busy?: boolean | undefined;
  error?: string | null | undefined;
  onRename: (title: string) => void;
}

export function BoardTitle({
  title,
  canEdit,
  busy,
  error,
  onRename,
}: BoardTitleProps): ReactElement {
  const { t } = useInterfaceLanguage();

  // `exactOptionalPropertyTypes`: `InlineEdit`'s own `busy?: boolean` and
  // `error?: string` accept no explicit `undefined` — only an OMITTED key
  // satisfies them, so `busy={busy}`/`error={error}` would not typecheck when
  // this component's own (necessarily wider) props are `undefined`. Same
  // shape as `ProfilePanel`'s `displayNameInlineEditProps`. `error === null`
  // (this component's "no error" spelling) collapses to the same omission.
  const inlineEditProps = {
    ...(busy !== undefined ? { busy } : {}),
    ...(error !== null && error !== undefined ? { error } : {}),
  };

  return (
    <h1
      aria-label={title}
      className="font-display text-xl font-semibold text-shadow-on-board"
    >
      {canEdit ? (
        <InlineEdit
          value={title}
          label={t('boardAdmin.title.label')}
          onCommit={onRename}
          commitOn="enter"
          emptyPolicy="reject-and-restore"
          controlClassName="text-on-board"
          {...inlineEditProps}
        />
      ) : (
        title
      )}
    </h1>
  );
}
